import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, ChevronDown, ChevronUp } from "lucide-react";
import { apiClient } from "@/lib/apiClient";

const STEPS = [
  "Google Cloud Console → Projekt wählen → «APIs & Dienste» → «Cloud Document AI API» aktivieren (Abrechnung muss aktiv sein).",
  "«Document AI» → «Prozessorgalerie» → «Invoice Parser» erstellen, Region «eu» (Europäische Union) wählen.",
  "Auf dem Prozessor die «ID» kopieren (nicht den Anzeigenamen) – sie steht in der Vorhersage-Endpunkt-URL.",
  "Zugang (Document AI akzeptiert keine API-Schlüssel): Dienstkonto mit Rolle «Document AI API User» anlegen und dessen JSON-Schlüssel einfügen. Falls deine Organisation Dienstkonto-Schlüssel sperrt: auf deinem PC «gcloud auth application-default login» ausführen und die Datei application_default_credentials.json (type: authorized_user) einfügen.",
  "«Speichern», dann «Live testen» – es wird eine Test-Rechnung (Total CHF 12.50) echt verarbeitet.",
];

/** Strukturierte Konfiguration für Google Document AI (Projekt, Prozessor, Region, Zugangsdaten). */
export function DocAIProviderForm({ info, onSaved }) {
  const meta = info?.meta || {};
  const [form, setForm] = useState({
    project_id: meta.project_id || "",
    processor_id: meta.processor_id || "",
    location: meta.location || "eu",
    processor_version: meta.processor_version || "",
    credentials: "",
  });
  const [saving, setSaving] = useState(false);
  const [guide, setGuide] = useState(!info?.is_set);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put("/admin/ai-providers/google_docai", { api_key: JSON.stringify(form) });
      toast.success("Google Document AI gespeichert");
      setForm({ ...form, credentials: "" });
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="docai-form">
      <button type="button" onClick={() => setGuide(!guide)} className="inline-flex items-center gap-1 text-[11px] text-sky-700 hover:underline" data-testid="docai-guide-toggle">
        {guide ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} Einrichtung in 5 Schritten
      </button>
      {guide && (
        <ol className="list-decimal pl-5 text-[11px] text-slate-600 space-y-0.5" data-testid="docai-guide">
          {STEPS.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Input value={form.project_id} onChange={set("project_id")} placeholder="Projekt-ID (z.B. buchhaltung-123456)" className="font-mono text-xs" data-testid="docai-project-id" />
        <Input value={form.processor_id} onChange={set("processor_id")} placeholder="Processor-ID (z.B. a1b2c3d4e5f6)" className="font-mono text-xs" data-testid="docai-processor-id" />
        <select value={form.location} onChange={set("location")} className="border border-slate-300 rounded-md px-2 text-xs bg-white h-9" data-testid="docai-location">
          <option value="eu">eu – Europäische Union</option>
          <option value="us">us – USA</option>
        </select>
      </div>
      <Input value={form.processor_version} onChange={set("processor_version")} placeholder="Processor-Version (optional, z.B. pretrained-invoice-v2.0-2023-12-06)" className="font-mono text-xs" data-testid="docai-processor-version" />
      <Textarea
        value={form.credentials}
        onChange={set("credentials")}
        placeholder={info?.is_set ? `Zugangsdaten unverändert lassen (aktuell: ${meta.credentials_mode || "–"}) – oder neu: {"type":"service_account",…} bzw. {"type":"authorized_user",…}` : 'Leer = Google-Vision-Zugang mitbenutzen (nur wenn dort ein Service-Account-JSON liegt) · {"type":"service_account",...} · {"type":"authorized_user",...}'}
        className="font-mono text-xs min-h-[70px]"
        data-testid="docai-credentials"
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] text-slate-500">Primäre Belegerkennung: Felder mit Konfidenz + Einzelpositionen. Ohne Konfiguration läuft weiterhin Vision OCR + LLM.</span>
        <Button onClick={save} disabled={saving || !form.project_id.trim() || !form.processor_id.trim()} size="sm" data-testid="ai-provider-save-google_docai">
          <Save className="h-3.5 w-3.5 mr-1" /> Speichern
        </Button>
      </div>
    </div>
  );
}
